import $ from 'jquery'
import rome from 'rome'
import deviceType from 'scoreboard/components/device-type'
import _ from 'lodash'
import moment from 'moment'
import momentHelper from 'scoreboard/components/moment-helper'
import CalendarControl from 'scoreboard/components/calendar-control'
import MarkedField from './marked-field'
import icon from 'scoreboard/components/icon'
import requiredValidator from 'scoreboard/components/validators/required-validator'
import KeyCodes from 'scoreboard/components/key-codes'
import dateValidatorFactory from 'scoreboard/components/validators/date-validator-factory'
import {isInDialog} from 'scoreboard/components/dialog'
import { isV6 } from 'scoreboard/components/version'

// Use the numeric format as a fallback for V5
// or when native date support is not available.
const dateFormat = () => {
	if (isV6() && nativeDate().isNative) {
		return momentHelper.isoDateFormat
	}
	return momentHelper.numericDateFormat
}

// http://stackoverflow.com/a/13025182/393274
function nativeDate() {
	if (isV6()) {
		let input = document.createElement('input')
		input.setAttribute('type', 'date')
		let isNative = input.type === 'date'
		return { isNative: isNative }
	}
	return { isNative: false }
}

// returns whether this is chrome on a desktop computer
function chromeOnDesktop() {
	return deviceType.chrome && !deviceType.isMobile()
}

function safariOnDesktop() {
	return deviceType.safari && !deviceType.isMobile()
}

// use rome if native dates aren't supported, or if using the desktop version
// of chrome, Edge, or desktop safari (since 14.1 introduced special date pickers)
let useRome = _.memoize(function () {
	return !nativeDate().isNative || chromeOnDesktop() || deviceType.firefox || deviceType.edge || safariOnDesktop()
})

/**
 * Transforms the .val() of the given element to a moment
 * @param {Element|jQuery} el the element
 * @param {Boolean} [strict=true] see https://momentjs.com/guides/#/parsing/strict-mode/
 */
function getMoment(el, strict) {
	let val = $(el).val()
	if (_.isEmpty(val)) {
		return null
	}

	let format = dateFormat()  // Use the dateFormat function to get the correct format
	if (_.isNil(strict)) {
		strict = momentHelper.STRICT
	}

	// Use the format returned by the dateFormat function to ensure
	// consistency with the expected date format.
	let parsedMoment = useRome() ? moment(val, format, strict) : val
	return parsedMoment.isValid() ? parsedMoment : null  // Check if the moment is valid
}

function getIsoDate(el, strict) {
	const moment = getMoment(el, strict)
	if (!moment) {
		return
	}

	return momentHelper.isoDate(moment)
}

function setValue(el, value) {
	// parse the date intelligently here.  if we don't do this, rome tries to
	// use the input format that was setup when the date selector was first
	// constructed, which isn't necessarily what we want.  this will handle
	// all sorts of different formats rather than just one format
	if (!_.isNil(value)) {
		value = moment(value)
	}

	// change the value in the popup
	rome(el, {horizontalAlignment: 'center'}).setValue(value)

	const format = dateFormat()
	const formatted = _.isNil(value) ? '' : value.format(format)
	$(el).val(formatted)
}

function dateValidator() {
	return dateValidatorFactory(dateFormat())
}

var DateInput = MarkedField.extend({

	events: {
		// Each time the input-date is clicked, rome.js tries to reset
		// the left position of the tooltip regardless of its current
		// position. So we need to run our position check on the event.
		'click .input-date' : 'positionTooltip'
	},

	/**
	 * Creates a date input widget which can be treated the same regardless
	 * of whether we are using a native date input to select the date.  Example
	 * usage:
	 *
	 * <input type="text" class="start-date">
	 *
	 * var input = new DateInput({
	 *     el: this.$('[name=startDate]'),
	 *     initialValue: '2015-06-26',
	 *     required: true
	 * })
	 * input.render()
	 * this.model.save({
	 *     date: input.getIsoDate()
	 * })
	 * input.remove() // important to do this when you're done!
	 *
	 * // get/set the values (can also access the underlying input element from the
	 * // DOM if you want)
	 * input.val('new value')
	 * var value = input.val()
	 *
	 * @param {Boolean} [options.required=false]
	 * @param {Object|Array} [options.validator] one or more validators to append
	 * to the list of validations.
	 *   dateValidator will always be applied.
	 *   requiredValidator will be applied when required=true
	 *
	 * @emits validated
	 * When the DateInput has been validated. Can be used to trigger
	 * an end date validation after the start validation is done.
	 * Listening to 'change' will cause too many validations to  be performed
	 * since the DateInput only does a validation when the (rome) calendar control is not visible.
	 *
	 */
	initialize: function(options) {
		const validators = this.getValidators(options)

		// don't send the required flag to Rome b/c that introduces behavior we don't want
		const opt = _.omit(options, 'required')
		this.skipMarkOnRender = true
		const extendedOptions = _.extend({
			markerContent: icon('error-marker'),
			// don't automatically replace the user's invalid input with a random date
			invalidate: false
		}, opt, { validator: validators })
		DateInput.__super__.initialize.call(this, extendedOptions)
	},

	getValidators: function(options) {
		const format = dateFormat()
		const dateValidator = dateValidatorFactory(format)

		const validators = [dateValidator]
		if (options?.required) {
			validators.push(requiredValidator)
		}
		if (options?.validator) {
			validators.push(...(_.isArray(options.validator) ? options.validator : [options.validator]))
		}

		return validators
	},

	render: function() {
		DateInput.__super__.render.call(this)

		var input = this.$('input')
		var value = this.options.initialValue || input.val()

		// forceRome should only be used for testing
		if (useRome() || this.options.forceRome) {
			var options = _.extend({}, this.options, {
				el: input[0],
				initialValue: value,
				dateFormat: dateFormat()  // Pass the correct date format to the CalendarControl
			})
			this.calendarControl = new CalendarControl(options).render()
			input.attr('type', 'text')
				.addClass('input-date')
				.prop('autocomplete', 'off')

			this.setupChangeListeners(input)

			if (value) {
				var m = moment(value)
				// change the value in the input box
				input.val(m.format(momentHelper.numericDateFormat))
			}
		} else {
			this.options.initialValue && input.val(this.options.initialValue)
			input.attr('type', 'date')
			if (value) {
				// Safari wants ISO format http://stackoverflow.com/a/11183982/393274
				input.val(momentHelper.isoDate(value))
			}
		}
		input.after('<span class="date-input-icon-wrapper">' + icon('calendar', 'type-icon') + '</span>')
		input.addClass('no-clear')

		// Change input type to "date" for consistent styling across browsers, due to
		// limited styling capabilities of input[type="text"] in the shadow DOM.
		// Design reference: https://www.figma.com/file/vtD2CPzINyTmGN8n1Y9D88/Library-webapp?type=design&node-id=7412-48525&mode=dev
		// https://stackoverflow.com/a/16106788
		if (isV6() && nativeDate().isNative) {
			input.attr('type', 'date')
			input.val(momentHelper.isoDate(value))
			input.attr('value', momentHelper.isoDate(value))
		}

		return this
	},

	/**
	 * All the things that need to happen when either the rome control
	 * is clicked or the native input value changes.
	 *
	 * @param input The input element holding the date
	 */
	setupChangeListeners: function (input) {

		// Invoked whenever rome initializes, could be on a click or focus event
		this.calendarControl.on('show', () => {
			this.positionTooltip()
		})

		this.calendarControl.on('hide', () => {
			this._tooltip && this._tooltip.addClass('is-not-visible')

			//some views may require to act only after the calendar input has
			//been hidden to ensure a better UI experience
			this.trigger('calendar-hide')
		})

		const inspectValue = (trigger) => {
			this.lastChangeTrigger = trigger
			// hide any error tooltips
			this.removeTooltip()
			// remove the error mark
			this._unmark()
			this.trigger('change')
			this.validate()
		}

		// Set month/year drop downs from the current date
		input.on('keyup', () => this.calendarControl.updateDropdownView())

		// Date change by typing into the input
		input.on('change', () => {
			inspectValue('blur')
		})

		// Date change by clicking a day on the calendar control
		this.calendarControl.on('change', function (moment) {
			// If using the native date input, set the value to the iso date format
			if (nativeDate().isNative) {
				input.val(moment.format(momentHelper.isoDateFormat))
			} else {
				input.val(moment.format(momentHelper.numericDateFormat))
			}
			// trigger an input (#11512 not change!) event on a dom element,
			// like a type="date" dom element would
			input.trigger('input')
			inspectValue('control')
		}.bind(this))

		// Validation is skipped while tooltip is open so must do it now
		this.calendarControl.on('hide', () => this._forcedValidation())
	},

	// Position the tooltip so that it doesn't get cut off on either of the edges of the viewport.
	positionTooltip: function() {
		var tooltip = $(this.calendarControl.rome.container)
		,	tooltipTargetPosition = this.calendarControl.el.getBoundingClientRect()
		,	bodyWidth = $('body').width()
		,	bodyHeight = $('body').height()
		,	tooltipWidth = tooltip.width()
		,	tooltipHeight = tooltip.height()
		,	currentTooltipLeft = parseInt(tooltip.css('left'))
		,	maxLeft = bodyWidth - tooltipWidth

		// The tooltip should be the last one in the DOM so it overlaps other tooltips
		$('body').append(tooltip)
		// The tooltip should show over top dialogs
		tooltip.addClass('over-dialog')

		// Is there not enough space on the right/left? Adjust the tooltip to fit.
		if (currentTooltipLeft > maxLeft) {
			tooltip.css('left', maxLeft)
		} else {
			tooltip.css('left',  currentTooltipLeft + 'px')
		}

		// Is there not enough space on the top/bottom? Asjust the tooltip to fit.
		// Append the appropriate .tooltip related classes based on whether the
		// tooltip should be placed above or below the target.
		if (bodyHeight < (tooltipTargetPosition.bottom + tooltipHeight)) {
			// It's bleeding off the bottom of the viewport, flip it to the top of
			// the tooltip target. 10px adjustment to get the .tooltip arrow on target.
			tooltip.css('top', tooltipTargetPosition.top - tooltipHeight - 10)
				.toggleClass('placement-above', true)
				.toggleClass('arrow-center', true)
				.removeClass('placement-below')
		} else {
			// There's enough space below the target, show it there.
			tooltip.toggleClass('placement-below', true)
				.toggleClass('arrow-center', true)
				.removeClass('placement-above')
		}

		tooltip.removeClass('is-not-visible')
			.css('display', 'inline-block')
		this._tooltip = tooltip

		if (isInDialog(this.$el)) {
			tooltip.addClass('over-dialog')
		}
	},

	removeTooltips: function() {
		_.invoke(this.calendarControl, 'rome.hide')
	},

	validate: function(value) {
		// this.lastChangeTrigger can be 'blur' or 'control'
		// based on direct input change or clicking the calendar date
		// console.log('last change triggered by:',this.lastChangeTrigger, 'on:', this.calendarControl.el.className)

		// the calendar control is open, so the user just clicked on date
		if (useRome() && this.calendarControl.isOpen()) {
			// don't perform validation right now
			return
		}
		delete this.lastChangeTrigger
		return this._forcedValidation(value)
	},

	_forcedValidation: function (v) {
		const value = v || this._val()
		var errorMsg = this.test(value)
		if (!errorMsg) {
			// if there is no error, remove the marking entirely. This differs from
			// the superclass behavior where we just make it a non error mark. Date
			// input fields will only have a marker if they're in an error state.
			this._unmark(true)
			this.removeTooltip()
		} else {
			this.error(errorMsg)
		}
		this.trigger('validated')
		return errorMsg
	},

	_changeErrorMessage: function(errorMsg) {
		// we don't really want to change the state of the error message here.
		// the real check will be done next tick by a function created in validate.
	},

	_onKeyup: function(evt) {
		if (evt.type == 'keyup') {
			var code = evt.keyCode
			if (!(code == KeyCodes.TAB || code == KeyCodes.SHIFT || code == KeyCodes.CTRL || code == KeyCodes.ALT || code == KeyCodes.CAPS_LOCK)) {
				// close the date browser when somebody starts typing since they
				// probably don't want it and it's annoying to have it open.

				// but don't close the date browser just because somebody presses
				// tab, shift, ctrl, alt, or caps lock
				// Exempting those keys makes navigation using the tab key work
				// correctly with the box opening and closing.
				if (useRome()) {
					this.calendarControl.getRome().hide()
				}
			}
		}
		DateInput.__super__._onKeyup.apply(this, arguments)
	},

	/**
	 * We want to remove any marking at all here since the only time a date input will be
	 * marked is if it has an error.
	 */
	clearError: function() {
		this._unmark()
	},

	/**
	 * Returns the current value shown in the date selector as a moment.
	 */
	getMoment: function(strict) {
		return getMoment(this.$('input'), strict)
	},

	/**
	 * Returns just the date portion of a date, suitable for serializing to the
	 * server.
	 */
	getIsoDate: function() {
		return momentHelper.isoDate(this.getMoment())
	},

	val(val) {
		let input = this.$('input')

		if (arguments.length === 0) {
			return input.val()
		}

		input.val(val)
	},

	remove: function() {
		DateInput.__super__.remove.apply(this, arguments)
		this.rome && this.rome.destroy()
		_.invokeMap([this.calendarControl], 'remove')
	}

})

_.extend(DateInput, {
	setValue,
	dateFormat
})

_.extend(DateInput, {
	getIsoDate,
	useRome,
	chromeOnDesktop
})
export default DateInput
export { getIsoDate }
export { useRome }
export { chromeOnDesktop }
export { dateFormat }
export { setValue }
export { nativeDate }
DateInput.__filename__ = 'components/date-input.js'
